const LIGHT_SCHEME = 'light' as const;
const DARK_SCHEME = 'dark' as const;
export const COLOR_SCHEME = {
  LIGHT: LIGHT_SCHEME,
  DARK: DARK_SCHEME,
} as const;
export const WIDESCREEN_WIDTH = 759;
export const SIDEBAR_DEFAULT_WIDTH = 272;
export const SIDEBAR_MIN_WIDTH = 224;
export const SIDEBAR_MAX_WIDTH = 400;
export const KEYBOARD_SHORTCUTS = {
  toggleOmniSearch: { id: 'toggleOmniSearch', keys: ['ctrl', 'k'] },
  // Avoid ctrl+shift+p (Firefox private window) and ctrl+alt+p (Firefox PDF
  // presentation mode; Ctrl+Alt also collides with AltGr on many layouts), and
  // ctrl+shift+enter (editor insert-above). Punctuation+shift is fine here:
  // `event.key` reports the shifted character ('.' → '>'), but the shortcut
  // manager falls back to `event.code` so the binding still matches.
  openCommands: { id: 'openCommands', keys: ['ctrl', 'shift', '.'] },
  // Ctrl+Alt maps to Cmd+Alt on macOS. Prefer letter chords that are uncommon
  // as AltGr dead-key sequences; still avoid ctrl+alt+p (see openCommands).
  newNote: { id: 'newNote', keys: ['ctrl', 'alt', 'n'] },
  newFolder: { id: 'newFolder', keys: ['ctrl', 'alt', 'f'] },
} as const;
export const SETTINGS_DEFAULT_COMMAND = {
  id: 'command::ui:open-settings',
  route: 'settings-general',
  title: 'Settings',
  keywords: ['settings', 'preferences'],
} as const;
export const SETTINGS_PAGE_DEFINITIONS = [
  {
    id: 'general',
    route: 'settings-general',
    commandId: 'command::ui:open-settings-general',
    commandTitle: 'Settings - General',
    commandKeywords: ['settings', 'preferences', 'general'],
  },
  {
    id: 'account',
    route: 'settings-account',
    commandId: 'command::ui:open-settings-account',
    commandTitle: 'Settings - Account',
    commandKeywords: [
      'settings',
      'account',
      'profile',
      'bio',
      'avatar',
      'social',
    ],
  },
  {
    id: 'workspaces',
    route: 'settings-workspaces',
    commandId: 'command::ui:open-settings-workspaces',
    commandTitle: 'Settings - Workspaces',
    commandKeywords: ['settings', 'preferences', 'workspaces', 'workspace'],
  },
  {
    id: 'recovery',
    route: 'settings-recovery',
    commandId: 'command::ui:open-settings-recovery',
    commandTitle: 'Settings - Recover',
    commandKeywords: [
      'settings',
      'recover',
      'recovery',
      'snapshot',
      'snapshots',
      'history',
      'backup',
      'restore',
    ],
  },
  {
    id: 'import-export',
    route: 'settings-import-export',
    commandId: 'command::ui:open-settings-import-export',
    commandTitle: 'Settings - Import / Export',
    commandKeywords: [
      'settings',
      'import',
      'export',
      'backup',
      'zip',
      'markdown',
      'transfer',
    ],
  },
] as const;
export type SettingsPageDefinition = (typeof SETTINGS_PAGE_DEFINITIONS)[number];
export type SettingsPageId = SettingsPageDefinition['id'];
export type SettingsRoute = SettingsPageDefinition['route'];
export function isSettingsRoute(route: string): route is SettingsRoute {
  return SETTINGS_PAGE_DEFINITIONS.some((page) => page.route === route);
}
export function isSettingsRouteInfo<RouteInfo extends { route: string }>(
  routeInfo: RouteInfo,
): routeInfo is Extract<RouteInfo, { route: SettingsRoute }> {
  return isSettingsRoute(routeInfo.route);
}
export const ASSET_LOCATION_PREFERENCES = [
  'assets-folder',
  'adjacent',
] as const;
export type AssetLocationPreference =
  (typeof ASSET_LOCATION_PREFERENCES)[number];
export function isAssetLocationPreference(
  value: unknown,
): value is AssetLocationPreference {
  return (
    typeof value === 'string' &&
    ASSET_LOCATION_PREFERENCES.includes(value as AssetLocationPreference)
  );
}

/** Handwriting fonts for cloud-collab comments (from Platenwell). */
export const COMMENT_FONTS = [
  { id: 'metafors', label: 'Metafors' },
  { id: 'another', label: 'Another' },
  { id: 'cute-font', label: 'Cute Font' },
  { id: 'lemon-tuesday', label: 'Lemon Tuesday' },
  { id: 'marker-sd', label: 'Marker SD' },
] as const;
export type CommentFontPreference = (typeof COMMENT_FONTS)[number]['id'];
export const DEFAULT_COMMENT_FONT: CommentFontPreference = 'metafors';
export const COMMENT_FONT_PREFERENCES = COMMENT_FONTS.map(
  (font) => font.id,
) as readonly CommentFontPreference[];
export function isCommentFontPreference(
  value: unknown,
): value is CommentFontPreference {
  return (
    typeof value === 'string' &&
    COMMENT_FONT_PREFERENCES.includes(value as CommentFontPreference)
  );
}

export function applyCommentFont(font: CommentFontPreference) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-comment-font', font);
  try {
    localStorage.setItem('sn-comment-font', font);
  } catch {
    // ignore
  }
}

/** Color themes ported from Platenwell. */
export const COLOR_THEMES = [
  {
    id: 'coffee',
    label: 'Coffee',
    mode: 'light',
    swatch: ['#e5ddd3', '#f3ebe0', '#442e23'],
  },
  {
    id: 'endicotter',
    label: 'Endicotter',
    mode: 'light',
    swatch: ['#dce2e8', '#ecf0f4', '#344e68'],
  },
  {
    id: 'epeithian',
    label: 'Epeithian',
    mode: 'light',
    swatch: ['#e0dee4', '#eeecf0', '#68448a'],
  },
  {
    id: 'sarizlight',
    label: 'Typo Light',
    mode: 'light',
    swatch: ['#f4f4f5', '#ffffff', '#0284c7'],
  },
  {
    id: 'elder',
    label: 'Elder',
    mode: 'dark',
    swatch: ['#0c100c', '#141a12', '#d6b052'],
  },
  {
    id: 'sariz',
    label: 'Typo Dark',
    mode: 'dark',
    swatch: ['#0f0f10', '#18181a', '#38bdf8'],
  },
  {
    id: 'snowynight',
    label: 'Snowy Night',
    mode: 'dark',
    swatch: ['#121820', '#1c242e', '#8eacc8'],
  },
] as const;
export type ColorThemePreference = (typeof COLOR_THEMES)[number]['id'];
export const DEFAULT_COLOR_THEME: ColorThemePreference = 'sarizlight';
export const COLOR_THEME_PREFERENCES = COLOR_THEMES.map(
  (theme) => theme.id,
) as readonly ColorThemePreference[];
export function isColorThemePreference(
  value: unknown,
): value is ColorThemePreference {
  return (
    typeof value === 'string' &&
    COLOR_THEME_PREFERENCES.includes(value as ColorThemePreference)
  );
}
export function colorThemeMode(id: ColorThemePreference): 'light' | 'dark' {
  const theme = COLOR_THEMES.find((entry) => entry.id === id);
  return theme?.mode ?? 'light';
}

/**
 * `localStorage` mirror of the applied color theme. The inline bootstrap
 * script reads it before the app bundle runs, so the first paint matches the
 * stored preference instead of the hard-coded default in `index.html`.
 */
export const COLOR_THEME_STORAGE_KEY = 'sn-color-theme';

export function applyColorTheme(theme: ColorThemePreference) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, theme);
  } catch {
    // ignore
  }
}

/**
 * The theme a reader link opens with until this browser has chosen one. A
 * shared note is usually read once, so it must not open in the app's
 * untouched Light default.
 */
export const READER_DEFAULT_COLOR_THEME: ColorThemePreference = 'sariz';

/**
 * The public reader route `/r/<token>`. It is served by the app's public
 * shell rather than the router, and every layer that cares about reader
 * links (the shell, the reader theme seed, the reader pre-paint script)
 * derives its check from this pattern so they cannot disagree.
 */
export const READER_ROUTE_PATTERN = /^\/r\/([^/]+)\/?$/;

/** The reader route token in `pathname`, or `undefined` if it is not one. */
export function matchReaderRouteToken(pathname: string): string | undefined {
  return pathname.match(READER_ROUTE_PATTERN)?.[1];
}

/** True when `pathname` points at a reader link. */
export function isReaderRoutePathname(pathname: string): boolean {
  return matchReaderRouteToken(pathname) !== undefined;
}

/** Editor body fonts ported from Platenwell. */
export const EDITOR_FONTS = [
  { id: 'aquifer', label: 'Aquifer' },
  { id: 'courier', label: 'Courier Prime' },
  { id: 'plex', label: 'IBM Plex Mono' },
  { id: 'libron', label: 'Libron' },
  { id: 'outfit', label: 'Outfit' },
  { id: 'special-elite', label: 'Special Elite' },
  { id: 'times-new-bastard', label: 'Times New Bastard' },
] as const;
export type EditorFontPreference = (typeof EDITOR_FONTS)[number]['id'];
export const DEFAULT_EDITOR_FONT: EditorFontPreference = 'libron';
export const EDITOR_FONT_PREFERENCES = EDITOR_FONTS.map(
  (font) => font.id,
) as readonly EditorFontPreference[];
export function isEditorFontPreference(
  value: unknown,
): value is EditorFontPreference {
  return (
    typeof value === 'string' &&
    EDITOR_FONT_PREFERENCES.includes(value as EditorFontPreference)
  );
}
export function applyEditorFont(font: EditorFontPreference) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-font', font);
  try {
    localStorage.setItem('sn-editor-font', font);
  } catch {
    // ignore
  }
}
/**
 * Maximum time a destructive editor lifecycle or file relocation waits for
 * the current tab's writes. Healthy saves settle quickly; reaching this bound
 * indicates a failed save whose in-memory content must remain recoverable.
 */
export const EDITOR_SAVE_DRAIN_TIMEOUT_MS = 5_000;

export const WORKSPACE_STORAGE_TYPE = {
  Help: 'helpfs',
  NativeFS: 'nativefs',
  Browser: 'browser',
  PrivateFS: 'privatefs',
  Github: 'github-storage',
  Memory: 'memory',
  Supabase: 'supabase',
} as const;
export type WorkspaceStorageType =
  (typeof WORKSPACE_STORAGE_TYPE)[keyof typeof WORKSPACE_STORAGE_TYPE];
export const FILE_STORAGE_MAX_FILE_SIZE_BYTES = {
  browser: 25 * 1024 * 1024,
  memory: 25 * 1024 * 1024,
  nativeFs: 250 * 1024 * 1024,
  supabase: 25 * 1024 * 1024,
} as const;

// Add all service names here
export const SERVICE_NAME = {
  browserErrorHandlerService: 'browser-error-handler',
  browserLocalStorageSyncDatabaseService: 'browser-local-storage-sync-database',
  browserRouterService: 'browser-router',
  commandDispatchService: 'command-dispatch',
  commandRegistryService: 'command-registry',
  editorService: 'editor',
  fileStorageIndexedDBService: 'file-storage-indexeddb',
  fileStorageMemoryService: 'file-storage-memory',
  fileStorageNativeFsService: 'file-storage-nativefs',
  fileStorageSupabaseService: 'file-storage-supabase',
  fileSystemService: 'file-system-service',
  idbDatabaseService: 'idb-database',
  memoryDatabaseService: 'memory-database',
  memoryRouterService: 'memory-router',
  memorySyncDatabaseService: 'memory-sync-database',
  navigationService: 'navigation-service',
  nodeErrorHandlerService: 'node-error-handler',
  noteSnapshotService: 'note-snapshot',
  shortcutService: 'shortcut',
  testErrorHandlerService: 'test-error-handler',
  userActivityService: 'user-activity',
  workbenchStateService: 'workbench-state',
  workspaceOpsService: 'workspace-ops',
  workspaceStateService: 'workspace-state',
  pmEditorService: 'pmEditorService',
} as const;
export const APP_MAIN_CONTENT_PADDING = 'px-4 py-4 pt-0 md:px-6';
/**
 * Left gutter for the editor page: wider than the default content padding so
 * the hover block handle ("+" and drag grip side by side) fits beside the
 * text. Sections rendered below the editor (e.g. linked mentions) share it so
 * their content stays aligned with the editor text.
 */
export const EDITOR_GUTTER_PADDING_LEFT = 'pl-10 md:pl-14';
export type ServiceName = (typeof SERVICE_NAME)[keyof typeof SERVICE_NAME];
/**
 * Sender tag on `event::file:update` / `event::file:force-update` events that
 * originated from a storage watcher observing changes made OUTSIDE this app
 * (sync tools, other editors). Consumers use it to react to external edits
 * without re-triggering on the app's own writes.
 */
export const EXTERNAL_FILE_CHANGE_SENDER_TAG = 'external-file-change';
export { browserHistoryStateEvents } from './browser-history-events';
export { commandExcludedServices, commandKeyToContext } from './command';
export { THEME_MANAGER_CONFIG } from './theme';
export {
  type DailyWordProgress,
  daysRemaining,
  GOAL_DEADLINE_KEY,
  GOAL_WORD_COUNT_KEY,
  isoDateOrNull,
  positiveIntOrNull,
  progressForToday,
  readWorkspaceGoals,
  todayIsoDate,
  WORDS_PER_DAY_GOAL_KEY,
  type WorkspaceGoalMetadata,
} from './workspace-goals';
// Note we are stuck with these names because of the indexeddb
export const DATABASE_TABLE_NAME = {
  // table for workspace related information like name, last modified, etc (dont contain actual Files)
  workspaceInfo: 'WorkspaceInfo',
  // a dump table for all the other information
  misc: 'MiscTable',
  // metadata of point-in-time note copies kept for user-driven recovery
  noteSnapshots: 'NoteSnapshots',
  // the (potentially large) content bodies of note snapshots, keyed by the
  // same snapshot id, so listing/eviction never has to load content
  noteSnapshotsContent: 'NoteSnapshotsContent',
  // latest not-yet-confirmed editor bodies, keyed by wsPath, so a tab crash
  // or failed write can still recover the user's text
  unsavedNoteDrafts: 'UnsavedNoteDrafts',
} as const;
